import { useState, useEffect, useMemo, useCallback } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";

// S2 endpoint — event admin routes
const EVENT_END_POINT = "event/admin/";

/**
 * Colour map for Mobiscroll calendar events, keyed on the lower-cased `Category`
 * the API computes — NOT on the source id sent in `categories`. `mypymble` is
 * kept as the legacy spelling of `manual`, which is also what an Edval event is
 * still labelled; nothing here asks for those, but a stray one draws in navy
 * rather than falling through to an undefined colour.
 */
const EVENT_COLORS: Record<string, string> = {
	edumate: "#26afd1",
	clipboard: "#26d153",
	manual: "#002f67",
	mypymble: "#002f67",
};

/**
 * Build the query string for the list endpoint.
 *
 * `categories` restricts the sources the API returns. Unsent, it answers with its
 * own default; a normal school week is ~1,717 events unfiltered, of which ~1,545
 * are Edval timetable bookings and Clipboard co-curricular sessions that carry no
 * approval block and cannot be managed here.
 */
function listQueryString(from: Date, to: Date, categories: string[]): string {
	const filter =
		'&filter={"from":"' +
		from.toISOString().split("T")[0] +
		'","to":"' +
		to.toISOString().split("T")[0] +
		'"}';
	return (
		"?limit=10000" +
		filter +
		"&categories=" +
		encodeURIComponent(categories.join(",")) +
		"&include_cancelled=false"
	);
}

/**
 * Transform a raw MongoDB Event document into the shape expected by
 * the Mobiscroll calendar and the Home page filter logic.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toCalendarEvent(doc: any): any {
	const category = (doc.Category || "mypymble").toLowerCase();
	return {
		...doc,
		id: doc._id,
		title: doc.Title || "",
		start: doc.StartDateTime,
		end: doc.EndDateTime,
		event_type: category,
		Source: category,
		location: doc.Location?.Name || doc.Location?.Address || "",
		color: EVENT_COLORS[category] || EVENT_COLORS.mypymble,
		Attendees: doc.Attendees || [],
	};
}

export type EventData = {
	_id: string;
	AdditionalNotes: string;
	Description?: { Long: string };
	MasterEventId?: number;
	Location: {
		Lat: number;
		Lon: number;
		Address: string;
	};
	Approval: {
		Required: boolean;
		Compulsory: boolean;
		FormUrl: string;
		MoreInfoUrl: string;
		MedicalCheck: boolean;
		EmergencyContact: boolean;
		DietaryCheck: boolean;
		PermissionLockDays: number;
		FirstReminderDays: number;
		SecondReminderDays: number;
		TermsAndConditions: string;
	};
	/** Chosen `EventTransport` dictionary entry (Code) and notes; null clears it. */
	Transport?: {
		Code: string;
		Notes: string | null;
		AcknowledgementRequired: boolean;
		AcknowledgementText: string | null;
	} | null;
	Banner: {
		_id: string;
		ImageUrl: string;
	};
	CarerApprovals: [id: string, value: string];
	Forms: [id: string, value: string];
	SelectedGroups: [string];
	Notices: [string];
};

interface EventListResponse {
	permissions: Record<string, boolean>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	events: any[];
}

interface EventDetailResponse {
	permissions: Record<string, boolean>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	event: any;
}

/** List query cache shape — permissions must live here so cache hits still hydrate `canEdit`. */
interface EventListQueryData {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	events: any[];
	permissions: Record<string, boolean>;
}

/** Detail query cache shape — same pattern for single-event cache hits. */
interface EventDetailQueryData {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	event: any;
	permissions: Record<string, boolean>;
}

// Parse API response, handling string/NaN edge cases
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseResponse = (res: any): any => {
	if (typeof res === "string") {
		const sanitizedJson = res.replace(/:\s*NaN/g, ": null");
		return JSON.parse(sanitizedJson);
	}
	return res;
};

// A factory, not a shared constant: the caller puts `events` straight into state,
// so a shared instance would leak one page's contents into the next empty result.
const emptyList = () => ({
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	events: [] as any[],
	permissions: {} as Record<string, boolean>,
});

/**
 * Fetch one page of the event list.
 *
 * Shared by the initial query and `getLazyData`, which built the same request
 * twice and would otherwise have to be kept in step by hand.
 *
 * With nothing selected it returns an empty list WITHOUT calling the API: an
 * empty `categories` reads as "no filter" at the other end and would hand back
 * every source, which is the opposite of what an empty selection means.
 */
async function fetchEventList(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	instance: any,
	from: Date,
	to: Date,
	categories: string[]
) {
	if (categories.length === 0) return emptyList();
	const endpoint = EVENT_END_POINT + listQueryString(from, to, categories);
	const res = await callPymbleApi(instance, "GET", endpoint);
	if (!res) return emptyList();
	const parsed: EventListResponse = parseResponse(res);
	return {
		events: parsed.events.map(toCalendarEvent),
		permissions: parsed.permissions ?? {},
	};
}

/**
 * Hook to fetch api data from event admin S2 endpoints.
 *
 * `categories` are the event sources to fetch. They are part of the query key, so
 * changing the Event Type filter refetches the visible range rather than sifting
 * what is already in hand — the excluded events never cross the wire.
 */
export function useEventManager(eventId?: string, categories: string[] = []) {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const queryClient = useQueryClient();

	// Mutable events state — the Event page mutates this directly for form editing
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [events, setEvents] = useState<any>([]);
	const [permissions, setPermissions] = useState<Record<string, boolean>>({});

	// Date range for list mode — changed by getLazyData
	const initialDateRange = useMemo(() => {
		const today = new Date();
		return {
			from: new Date(today.getFullYear(), today.getMonth(), 1),
			to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
		};
	}, []);

	const [dateRange, setDateRange] = useState(initialDateRange);

	// Sorted so that two selections with the same members share one cache entry.
	const categoryKey = useMemo(
		() => [...categories].sort().join(","),
		[categories]
	);

	// Query: event list (Home page, no eventId)
	const listQuery = useQuery({
		queryKey: [
			"events",
			"list",
			"v2",
			dateRange.from.toISOString(),
			dateRange.to.toISOString(),
			categoryKey,
		],
		queryFn: () =>
			fetchEventList(instance, dateRange.from, dateRange.to, categories),
		enabled: isAuthenticated && !eventId,
	});

	// Query: single event (Event page, with eventId)
	const eventQuery = useQuery({
		queryKey: ["event", "v2", eventId],
		queryFn: async () => {
			// supervisors=1 adds `Supervisors` (sorted by name) for "Event Owner"
			const endpoint =
				EVENT_END_POINT + eventId + "?attendees=1&supervisors=1";
			const res = await callPymbleApi(instance, "GET", endpoint);
			if (!res) return null;
			const parsed: EventDetailResponse = parseResponse(res);
			return {
				event: parsed.event,
				permissions: parsed.permissions ?? {},
			};
		},
		enabled: isAuthenticated && !!eventId,
	});

	// Seed events state from query results
	useEffect(() => {
		const d = listQuery.data as unknown;
		if (d == null) return;
		const payload = d as EventListQueryData;
		setEvents(payload.events);
		setPermissions(payload.permissions);
	}, [listQuery.data]);

	useEffect(() => {
		const d = eventQuery.data;
		if (d == null) return;
		const payload = d as EventDetailQueryData;
		setEvents(payload.event);
		setPermissions(payload.permissions);
	}, [eventQuery.data]);

	// getLazyData — called by Mobiscroll onPageLoading with new date range.
	// Uses queryClient.fetchQuery so it returns a promise (preserving the
	// existing interface that Home.tsx relies on).
	const getLazyData = useCallback(
		async (from_date: Date, to_date: Date) => {
			const data = await queryClient.fetchQuery({
				queryKey: [
					"events",
					"list",
					"v2",
					from_date.toISOString(),
					to_date.toISOString(),
					categoryKey,
				],
				queryFn: () =>
					fetchEventList(instance, from_date, to_date, categories),
			});
			setEvents(data.events);
			setPermissions(data.permissions);
			setDateRange({ from: from_date, to: to_date });
		},
		[instance, queryClient, categories, categoryKey]
	);

	// Mutation: update event
	const updateMutation = useMutation({
		mutationFn: async (eventData: EventData) => {
			const endpoint = EVENT_END_POINT + eventData["_id"];
			return callPymbleApi(instance, "PUT", endpoint, {
				data: eventData,
			});
		},
	});

	const updateEvents = async (eventData: EventData) => {
		try {
			return await updateMutation.mutateAsync(eventData);
		} catch (error) {
			console.error(`ERROR: Could not update event.  ${error}`);
			throw error;
		}
	};

	// For single-event mode, derive from whether events state has been
	// seeded (object, not the initial []).  The useEffect that copies
	// eventQuery.data → events runs one render after isPending flips,
	// so checking isPending alone would let the form render with [].
	const eventsLoaded = eventId
		? events != null && typeof events === "object" && !Array.isArray(events)
		: !listQuery.isPending;

	const canEdit = permissions?.event_admin === true;

	return {
		events,
		eventsLoaded,
		eventLoadError: eventQuery.error,
		eventSaving: updateMutation.isPending,
		canEdit,
		getLazyData,
		updateEvents,
	};
}
